/* eslint-disable @typescript-eslint/no-explicit-any */
import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ProjectModel } from 'model/ProjectModel'
import {
  isTheProjectLocked as getProjectCurrentStatus,
  normalizeProjectState,
} from '../../../store/Project/ProjectStateUtils'
import { addAsyncOperationStatus } from './addAsyncOperationStatus'
import { fetchBomItem } from './asyncActions/fetchBomItem'
import { fetchProject } from './asyncActions/fetchProject'
import { subscribeToProjectNotifications } from './asyncActions/subscribeToUpdates'
import {
  activeProjectReplacer,
  bomItemReplacer,
  projectStateReplacer,
} from './bomItem/bomItemReplacer'
import { bomItemWorkingStepsReplacer } from './bomItem/bomItemWorkingStepsReplacer'
import { addWorkingStepToBomItem } from './bomItem/addWorkingStepToBomItem'
import { deleteBomItemFromStore } from './bomItem/deleteBomItemFromStore'
import { removePricesFromAllBoMItems } from './bomItem/removePricesFromAllBoMItems'
import { setBoMItemFocus } from './bomItem/setBoMItemFocus'
import { openOrCloseAllHeaders, setBoMItemOpen } from './bomItem/setBoMItemOpen'
import {
  setSearchedBomItem,
  setSearchedBomItemByRowNumber,
} from './bomItem/setSearchedBomItem'
import { removeAllFilters } from './filterActions/removeAllFilters'
import { setFilterProperty } from './filterActions/setFilterProperty'
import { toggleActiveFilter } from './filterActions/toggleActiveFilter'
import { refreshIssuesFilter } from './filters/refreshIssuesFilter'
import { refreshMaterialFilter } from './filters/refreshMaterialFilter'
import { refreshWorkingStepFilter } from './filters/refreshWorkingStepFilter'
import { updateHeadersFilteredParts } from './filters/updateHeadersFilteredParts'
import {
  changeProjectView,
  changeProjectViewIfInView,
} from './project/changeProjectView'
import { setOperationProgress } from './project/setOperationProgress'
import { updateOperationsFlags } from './project/updateOperationsFlags'
import { projectInitialState } from './ProjectInitialState'
import { removeWorkingStepFromBomItem } from './bomItem/removeWorkingStepFromBomItem'
import { resetBomItemFinancials } from './bomItem/resetBomItemFinancials'

export function getActionPrefix(action: AnyAction, aditionalSuffix?: string) {
  let actionName: string = action.type.substring(
    0,
    action.type.lastIndexOf('/')
  )

  if (aditionalSuffix) {
    actionName = actionName.concat('/', aditionalSuffix)
  }

  return actionName
}

const projectSlice = createSlice({
  name: 'project',
  initialState: projectInitialState,
  reducers: {
    unloadProject: () => {
      return projectInitialState
    },
    saveProject: (state, action: PayloadAction<ProjectModel>) => {
      state.activeProject = action.payload
    },
    setBomItemProperties: bomItemReplacer,
    resetBomItemFinancials: resetBomItemFinancials,
    setBomItemWorkingSteps: bomItemWorkingStepsReplacer,
    addWorkingStep: addWorkingStepToBomItem,
    removeWorkingStep: removeWorkingStepFromBomItem,
    setActiveProjectProperties: activeProjectReplacer,
    setProjectStateProperties: projectStateReplacer,
    setOpen: setBoMItemOpen,
    setBomItemFocus: setBoMItemFocus,
    setBomItemInSearch: setSearchedBomItem,
    setBomItemInSearchByRowNumber: setSearchedBomItemByRowNumber,
    setSelectedBomItems: (state, action: PayloadAction<string[]>) => {
      state.selectedBomItemIds = action.payload
    },
    setSelectedHeaderIds: (state, action: PayloadAction<string[]>) => {
      state.selectedHeaderIds = action.payload
    },
    removeFromSelectedHeaderIds: (state, action: PayloadAction<string>) => {
      state.selectedHeaderIds = state.selectedHeaderIds.filter(
        (id) => id !== action.payload
      )
    },
    removeFromSelectedBomItems: (state, action: PayloadAction<string>) => {
      state.selectedBomItemIds = state.selectedBomItemIds.filter(
        (id) => id !== action.payload
      )
    },
    removeAsSelectedBomItems: (state, action: PayloadAction<string[]>) => {
      state.selectedBomItemIds = state.selectedBomItemIds.filter(
        (id) => !action.payload.includes(id)
      )
    },

    clearSelectedBomItems: (state) => {
      state.selectedBomItemIds = []
    },
    includeInSelectedBomItems: (state, action: PayloadAction<string>) => {
      state.selectedBomItemIds.push(action.payload)
    },
    includeAsSelected: (state, action: PayloadAction<string[]>) => {
      state.selectedBomItemIds = state.selectedBomItemIds.concat(action.payload)
    },
    updateHeadersFilteredParts: updateHeadersFilteredParts,
    refreshWorkingStepFilter: refreshWorkingStepFilter,
    refreshMaterialFilter: refreshMaterialFilter,
    refreshIssuesFilter: refreshIssuesFilter,
    removePrices: removePricesFromAllBoMItems,
    openOrCloseAllHeaders: openOrCloseAllHeaders,
    changeProjectView: changeProjectView,
    changeProjectViewIfInView: changeProjectViewIfInView,
    uploadProgress: setOperationProgress,
    deleteBomItem: deleteBomItemFromStore,
    toggleActiveFilter: toggleActiveFilter,
    setFilter: setFilterProperty,
    removeAllFilters: removeAllFilters,
    updateOperationsFlags: updateOperationsFlags,
  },

  extraReducers: (builder) => {
    builder.addCase(fetchProject.fulfilled, (state, action) => {
      if (
        state.activeProject?.id &&
        action.payload['id'] !== state.activeProject?.id
      ) {
        // case the received id is different that the current project id, ignore it and return
        // the current state
        return state
      }
      state = {
        ...state,
        ...normalizeProjectState(
          action.payload,
          state,
          state.selectedBomItemIds[0]
        ),
      }

      const { projectIsLocked, projectStatus } = getProjectCurrentStatus(
        state.activeProject
      )

      state.activeProject.status = projectStatus
      state.activeProject.isLocked = projectIsLocked
      state = updateHeadersFilteredParts(state)
      state = refreshWorkingStepFilter(state)
      state = refreshMaterialFilter(state)
      state = refreshIssuesFilter(state)

      return state
    })
    builder.addCase(fetchBomItem.fulfilled, bomItemReplacer)
    builder.addCase(subscribeToProjectNotifications.fulfilled, () => {
      console.info('subscribed to updates')
    })

    addAsyncOperationStatus(builder)
  },
})

export default projectSlice.reducer
export const newProjectActions = projectSlice.actions
